import React, { useState, useEffect } from 'react';
import { schoolData } from '../../lib/supabase';

const SchoolInfoPage = ({ onBack, onTabChange }) => {
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [schoolNews, setSchoolNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('info');

  useEffect(() => {
    loadSchoolData();
  }, []);

  const loadSchoolData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load school information from existing Supabase table
      const { data: info, error: infoError } = await schoolData.getSchoolInfo();
      if (infoError) {
        console.error('Error loading school info:', infoError);
        setError('Failed to load school information');
      } else {
        setSchoolInfo(info);
      }

      // Load school news from existing Supabase table
      const { data: news, error: newsError } = await schoolData.getSchoolNews();
      if (newsError) {
        console.error('Error loading school news:', newsError);
        setError('Failed to load school news');
      } else {
        setSchoolNews(news || []);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="module-container">
        <div className="module-header">
          <h2 className="module-title">學校資訊</h2>
        </div>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div className="loading-spinner"></div>
          <p>載入中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="module-container">
        <div className="module-header">
          <h2 className="module-title">學校資訊</h2>
          <button className="back-button" onClick={onBack}>
            返回
          </button>
        </div>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>
          <p>錯誤: {error}</p>
          <button className="btn btn-primary" onClick={loadSchoolData}>
            重試
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="module-container">
      <div className="module-header">
        <h2 className="module-title">學校資訊</h2>
        <button className="back-button" onClick={onBack}>
          返回
        </button>
      </div>

      {/* Tab Navigation */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', gap: '1rem', borderBottom: '2px solid #e5e7eb' }}>
          <button
            className={`btn ${activeTab === 'info' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('info')}
            style={{ borderRadius: '8px 8px 0 0' }}
          >
            基本資料
          </button>
          <button
            className={`btn ${activeTab === 'news' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('news')}
            style={{ borderRadius: '8px 8px 0 0' }}
          >
            學校新聞
          </button>
        </div>
      </div>

      {/* School Information Tab */}
      {activeTab === 'info' && schoolInfo && (
        <div>
          {/* School Header */}
          <div style={{
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            color: 'white',
            padding: '2rem',
            borderRadius: '12px',
            marginBottom: '2rem',
            textAlign: 'center'
          }}>
            <h1 style={{ fontSize: '2.5rem', margin: '0 0 1rem 0', fontWeight: '700' }}>
              {schoolInfo.name}
            </h1>
            <p style={{ fontSize: '1.2rem', margin: '0 0 1rem 0', opacity: '0.9' }}>
              {schoolInfo.name_en}
            </p>
            <p style={{ fontSize: '1rem', margin: '0', opacity: '0.8' }}>
              {schoolInfo.motto} • {schoolInfo.motto_en}
            </p>
          </div>

          {/* Basic Information Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem'
          }}>
            {/* Contact Information */}
            <div style={{
              backgroundColor: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{ color: '#1f2937', marginBottom: '1rem', fontSize: '1.2rem' }}>
                📞 聯絡資訊
              </h3>
              <div style={{ lineHeight: '1.8' }}>
                <p><strong>地址:</strong> {schoolInfo.address}</p>
                <p><strong>Address:</strong> {schoolInfo.address_en}</p>
                <p><strong>電話:</strong> {schoolInfo.phone}</p>
                <p><strong>傳真:</strong> {schoolInfo.fax}</p>
                <p><strong>電郵:</strong> {schoolInfo.email}</p>
                <p><strong>網站:</strong> {schoolInfo.website}</p>
              </div>
            </div>

            {/* School Details */}
            <div style={{
              backgroundColor: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{ color: '#1f2937', marginBottom: '1rem', fontSize: '1.2rem' }}>
                🏫 學校詳情
              </h3>
              <div style={{ lineHeight: '1.8' }}>
                <p><strong>創校年份:</strong> {schoolInfo.established}</p>
                <p><strong>校長:</strong> {schoolInfo.principal}</p>
                <p><strong>Principal:</strong> {schoolInfo.principal_en}</p>
                <p><strong>學校類型:</strong> {schoolInfo.type}</p>
                <p><strong>宗教背景:</strong> {schoolInfo.religion}</p>
                <p><strong>教學語言:</strong> {schoolInfo.medium}</p>
              </div>
            </div>

            {/* Statistics */}
            <div style={{
              backgroundColor: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{ color: '#1f2937', marginBottom: '1rem', fontSize: '1.2rem' }}>
                📊 統計資料
              </h3>
              <div style={{ lineHeight: '1.8' }}>
                <p><strong>學生人數:</strong> {schoolInfo.total_students?.toLocaleString()}</p>
                <p><strong>教師人數:</strong> {schoolInfo.total_teachers}</p>
                <p><strong>設施數量:</strong> {Array.isArray(schoolInfo.facilities) ? schoolInfo.facilities.length : 0}</p>
                <p><strong>科目數量:</strong> {Array.isArray(schoolInfo.subjects) ? schoolInfo.subjects.length : 0}</p>
              </div>
            </div>
          </div>

          {/* Facilities */}
          {schoolInfo.facilities && Array.isArray(schoolInfo.facilities) && (
            <div style={{
              backgroundColor: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb',
              marginBottom: '2rem'
            }}>
              <h3 style={{ color: '#1f2937', marginBottom: '1rem', fontSize: '1.2rem' }}>
                🏢 學校設施
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem'
              }}>
                {schoolInfo.facilities.map((facility, index) => (
                  <div key={index} style={{
                    padding: '0.75rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '6px',
                    textAlign: 'center',
                    border: '1px solid #e5e7eb'
                  }}>
                    {facility}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Achievements */}
          {schoolInfo.achievements && Array.isArray(schoolInfo.achievements) && (
            <div style={{
              backgroundColor: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb',
              marginBottom: '2rem'
            }}>
              <h3 style={{ color: '#1f2937', marginBottom: '1rem', fontSize: '1.2rem' }}>
                🏆 學校成就
              </h3>
              <div style={{ lineHeight: '1.8' }}>
                {schoolInfo.achievements.map((achievement, index) => (
                  <p key={index} style={{
                    padding: '0.5rem 0',
                    borderBottom: index < schoolInfo.achievements.length - 1 ? '1px solid #e5e7eb' : 'none'
                  }}>
                    • {achievement}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Social Media */}
          {schoolInfo.social_media && (
            <div style={{
              backgroundColor: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{ color: '#1f2937', marginBottom: '1rem', fontSize: '1.2rem' }}>
                🌐 社交媒體
              </h3>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                {Object.entries(schoolInfo.social_media).map(([platform, url]) => (
                  <a
                    key={platform}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      textDecoration: 'none',
                      borderRadius: '6px',
                      fontWeight: '500',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                  >
                    {platform.charAt(0).toUpperCase() + platform.slice(1)}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* School News Tab */}
      {activeTab === 'news' && (
        <div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '1.5rem'
          }}>
            {schoolNews.map((news) => (
              <div key={news.id} style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #e5e7eb'
              }}>
                {news.image && (
                  <img
                    src={news.image}
                    alt={news.title}
                    style={{
                      width: '100%',
                      height: '200px',
                      objectFit: 'cover'
                    }}
                  />
                )}
                <div style={{ padding: '1.5rem' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem'
                  }}>
                    <span style={{
                      backgroundColor: news.featured ? '#f59e0b' : '#6b7280',
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: '500'
                    }}>
                      {news.featured ? '精選' : news.category}
                    </span>
                    <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                      {formatDate(news.date)}
                    </span>
                  </div>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    color: '#1f2937',
                    margin: '0 0 0.75rem 0',
                    lineHeight: '1.4'
                  }}>
                    {news.title}
                  </h3>
                  <p style={{
                    color: '#6b7280',
                    lineHeight: '1.6',
                    margin: '0 0 1rem 0'
                  }}>
                    {news.content}
                  </p>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '0.875rem',
                    color: '#6b7280'
                  }}>
                    <span>作者: {news.author}</span>
                    {news.featured && (
                      <span style={{ color: '#f59e0b', fontWeight: '500' }}>
                        ⭐ 精選文章
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {schoolNews.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: '#6b7280'
            }}>
              <p>暫無學校新聞</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SchoolInfoPage;
